import { InlineFormattedNumber, getColorBarColumn, type ColorBarProps, type Column } from '@talos/kyoko';
import type { BigSource } from 'big.js';
import { get } from 'lodash-es';
import type React from 'react';
import { useMemo } from 'react';
import styled, { useTheme } from 'styled-components';
import { calculateDurationBarWidth, calculateRelativeBarWidth } from '../../../utils';

export type PreTradeAnalyticName =
  | 'Start Time'
  | 'End Time'
  | 'Spread'
  | 'Volume'
  | 'Participation Rate'
  | 'Volatility'
  | 'Duration';

export interface PreTradePredictionRow {
  name: PreTradeAnalyticName;
  interval: BigSource | null;
  daily: BigSource | null;
  vs: BigSource | null;
}

// Styled span to match InlineFormattedNumber text color for Duration string values
const DurationText = styled.span`
  color: ${({ theme }) => theme.colorStackedNumberHighlight};
`;

// TODO: Get these from theme instead
const BAR_COLORS: Partial<Record<PreTradeAnalyticName, string>> = {
  Spread: '#314A8C85',
  Volume: '#DAA54185',
  'Participation Rate': '#B351B385',
  Volatility: '#C9444285',
  Duration: '#65656585',
  // Start Time, End Time: no bars
};

function getColorBarProps(colId: string, data: PreTradePredictionRow): ColorBarProps {
  const barColor = BAR_COLORS[data.name] ?? '';
  let barWidth = 0;
  const { name, interval, daily } = data;
  const isNumericType = typeof interval !== 'string' && typeof daily !== 'string';

  if (name === 'Duration' && typeof interval === 'string' && typeof daily === 'string') {
    // Duration row uses string values - convert to ms for bar calculation
    if (colId === 'interval' || colId === 'daily') {
      barWidth = calculateDurationBarWidth(interval, daily, colId);
    }
    // VS column shows percentage only, no bar (barWidth stays 0)
  } else if (isNumericType && daily != null && interval != null) {
    switch (colId) {
      case 'interval':
        barWidth = calculateRelativeBarWidth(interval, daily);
        break;
      case 'daily':
        barWidth = calculateRelativeBarWidth(daily, interval);
        break;
      case 'vs':
        // VS column shows percentage only, no bar
        barWidth = 0;
        break;
    }
  }

  let unit = '';
  if (name === 'Participation Rate' || colId === 'vs') {
    unit = '%';
  } else if (name === 'Spread' || name === 'Volatility') {
    unit = 'BPS';
  }
  const value = get(data, colId);

  let content: React.ReactNode;
  if (value == null) {
    content = '-';
  } else if (isNumericType || colId === 'vs') {
    content = <InlineFormattedNumber number={value} currency={unit} increment="0.01" align="right" />;
  } else {
    // Duration row string values - use styled text to match InlineFormattedNumber color
    content = <DurationText>{value}</DurationText>;
  }

  return {
    content,
    value,
    barColor,
    barWidth,
  };
}

export const usePreTradePredictionColumns = () => {
  const theme = useTheme();

  return useMemo(() => {
    const columns: Column[] = [
      {
        type: 'custom',
        field: 'name',
        title: 'Analytic',
        maxWidth: 170,
        flex: 1,
        frozen: true,
        suppressHeaderMenuButton: true,
        params: {
          cellRenderer: ({ value }: { value: PreTradeAnalyticName }) => {
            return <span style={{ color: theme.colors.gray['090'] }}>{value}</span>;
          },
        },
      },
      {
        ...getColorBarColumn({ colId: 'interval', getColorBarProps }),
        title: 'Interval',
        flex: 2,
        frozen: true,
        suppressHeaderMenuButton: true,
      },
      {
        ...getColorBarColumn({ colId: 'daily', getColorBarProps }),
        title: 'Daily',
        flex: 2,
        frozen: true,
        suppressHeaderMenuButton: true,
      },
      {
        ...getColorBarColumn({ colId: 'vs', getColorBarProps }),
        title: 'VS',
        minWidth: 80,
        flex: 1,
        frozen: true,
        suppressHeaderMenuButton: true,
      },
    ];
    return columns;
  }, [theme.colors.gray]);
};
